import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { setAuth } from "../lib/api";
import { Leaf, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      setAuth(data.token, data.admin);
      toast.success(`Welcome back, ${data.admin.name.split(" ")[1] || data.admin.name}`);
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="admin-login-page">
      <div className="hidden md:block relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/15319047/pexels-photo-15319047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
          alt="Dietician"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[hsl(var(--brand-ink))]/30" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs tracking-[0.3em] uppercase opacity-80">Nourish · Admin</div>
          <div className="font-display text-4xl mt-3 max-w-sm">A quiet place to care for every client.</div>
        </div>
      </div>
      <div className="flex items-center justify-center bg-[hsl(var(--brand-cream))] p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--brand-sage))] text-white grid place-items-center">
              <Leaf className="w-5 h-5" />
            </div>
            <div className="font-display text-2xl">Nourish</div>
          </div>
          <h1 className="font-display text-4xl tracking-tight text-[hsl(var(--brand-ink))]">Welcome back.</h1>
          <p className="text-[hsl(var(--brand-muted))] mt-2">Sign in to manage your clients and plans.</p>
          <label className="block mt-8 text-sm text-[hsl(var(--brand-muted))]">Email</label>
          <input
            className="w-full mt-1 px-4 py-3 rounded-md border border-[hsl(var(--brand-border))] bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-sage))]/30 focus:border-[hsl(var(--brand-sage))]"
            type="email"
            name="admin-email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email-input"
          />
          <label className="block mt-4 text-sm text-[hsl(var(--brand-muted))]">Password</label>
          <input
            className="w-full mt-1 px-4 py-3 rounded-md border border-[hsl(var(--brand-border))] bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-sage))]/30 focus:border-[hsl(var(--brand-sage))]"
            type="password"
            name="admin-password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="login-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full bg-[hsl(var(--brand-sage))] text-white py-3 rounded-md hover:bg-[hsl(var(--brand-sage-hover))] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="login-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sign in
          </button>
          <p className="text-xs text-[hsl(var(--brand-muted))] mt-6 text-center">
            This site is private. Clients receive their own unique link.
          </p>
        </form>
      </div>
    </div>
  );
}
